import { useEffect, useState } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { useAddons, usePackages } from '../../api/catalog';

/** Add-ons — table + new-addon drawer. */
export function Addons() {
  const { data: addons = [], isLoading } = useAddons();
  const { data: packages = [] } = usePackages();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="Add-ons"
        subtitle="À-la-carte extras subscribers can buy on top of any package."
        action={<Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>New add-on</Button>}
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3 min-w-[280px]">Add-on</th>
                <th className="text-left font-medium px-4 py-3">Unit</th>
                <th className="text-right font-medium px-4 py-3">Price</th>
                <th className="text-left font-medium px-4 py-3">Billing</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell min-w-[260px]">Compatible tiers</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">In use</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-24 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-56 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-32 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : addons.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No add-ons yet. Create one to offer à-la-carte extras.
                  </td>
                </tr>
              ) : (
                addons.map((a) => {
                const compatibleNames = packages
                  .filter((p) => a.compatiblePackageIds.includes(p.id))
                  .map((p) => p.name);
                const isAllTiers = compatibleNames.length === packages.length;
                return (
                  <tr key={a.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3">
                      <div className="font-medium text-zinc-950 dark:text-zinc-50">{a.name}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 mono">{a.internalCode}</div>
                      <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-300 max-w-md leading-snug">
                        {a.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs whitespace-nowrap">{a.unitLabel}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Money minor={a.amount} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                      {a.billing === 'recurring' ? 'Recurring' : 'One-time'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {isAllTiers ? (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">All tiers</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {compatibleNames.map((name) => (
                            <span
                              key={name}
                              className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{a.inUseCount}</td>
                    <td className="px-4 py-3">
                      <Badge tone={a.status === 'active' ? 'success' : 'neutral'}>
                        {a.status === 'active' ? 'Active' : 'Draft'}
                      </Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                          Edit
                        </button>
                        <button
                          aria-label={`More actions for ${a.name}`}
                          className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
        Add-ons are charged on top of the base subscription. Capped-tier add-ons (extra products /
        invoices) only apply to Starter / Basic / Regular — Standard and above already include
        unlimited products and invoices.
      </p>

      <NewAddonDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

function NewAddonDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: packages = [] } = usePackages();
  const [billing, setBilling] = useState<'recurring' | 'one_time'>('recurring');
  const [status, setStatus] = useState<'active' | 'draft'>('active');
  const [tierSelection, setTierSelection] = useState<Set<string>>(new Set());
  useEffect(() => {
    setTierSelection(new Set(packages.map((p) => p.id)));
  }, [packages]);

  function toggleTier(id: string) {
    const next = new Set(tierSelection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setTierSelection(next);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New add-on"
      width="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onClose}>Save</Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input label="Name" required defaultValue="Extra location" />
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Description</label>
          <textarea
            rows={2}
            defaultValue="Each location adds 1 to your max."
            className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Unit</label>
          <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
            <option>per location per month</option>
            <option>per user per month</option>
            <option>per 1k products per month</option>
            <option>per 1k invoices per month</option>
            <option>per 1k SMS</option>
            <option>flat per month</option>
            <option>one-time</option>
          </select>
        </div>
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Currency</label>
            <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
              <option>BDT</option>
              <option>USD</option>
            </select>
          </div>
          <Input label="Price" required defaultValue="100" />
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Billing</span>
          <div className="space-y-2">
            <Radio name="billing" checked={billing === 'recurring'} onChange={() => setBilling('recurring')} label="Recurring with subscription" />
            <Radio name="billing" checked={billing === 'one_time'} onChange={() => setBilling('one_time')} label="One-time charge at purchase" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Compatible packages
            <span className="ml-1.5 text-xs font-normal text-zinc-500 dark:text-zinc-400">
              ({tierSelection.size} of {packages.length} selected)
            </span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            {packages.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tierSelection.has(p.id)}
                  onChange={() => toggleTier(p.id)}
                  className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>

        <Input label="Max purchasable" hint="per subscription" defaultValue="10" />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</span>
          <div className="space-y-2">
            <Radio name="status" checked={status === 'active'} onChange={() => setStatus('active')} label="Active" />
            <Radio name="status" checked={status === 'draft'} onChange={() => setStatus('draft')} label="Draft" />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
